import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { EnvironmentOutlined } from "@ant-design/icons";
import { Button, Card, Empty, Tag } from "antd";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import Heading from "../../components/Heading";
import { getAllNonServiceablePincodes } from "../../services/api/pincodeServiceabilityApi";
import {
  buildPincodeMapUrl,
  getHubCountLabel,
  getHubFallbackName,
  getNoResultsDescription,
  getServiceCountLabel,
  PINCODE_SEARCH_COVERAGE_CHIPS,
  PINCODE_SEARCH_COPY,
  PINCODE_SEARCH_FIELD_KEYS,
  PINCODE_SEARCH_MAP,
  PINCODE_SEARCH_NAVIGATION,
  PINCODE_SEARCH_STATUS,
} from "../../constants/pincodeSearchConstants";
import "../SearchResults/shared.scss";
import "./style.scss";

const pickFirstFieldValue = (item, keys) => {
  const matchedKey = keys.find(
    (key) => item?.[key] !== undefined && item?.[key] !== null
  );

  if (!matchedKey) return null;
  return item?.[matchedKey];
};

const toFiniteNumber = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const extractCoordinates = (item) => {
  const lat = toFiniteNumber(
    pickFirstFieldValue(item, PINCODE_SEARCH_FIELD_KEYS.LATITUDE)
  );
  const lng = toFiniteNumber(
    pickFirstFieldValue(item, PINCODE_SEARCH_FIELD_KEYS.LONGITUDE)
  );

  if (lat === null || lng === null) return null;
  return { lat, lng };
};

const extractPincode = (item) => {
  const pincode = pickFirstFieldValue(item, PINCODE_SEARCH_FIELD_KEYS.PINCODE);
  return String(pincode ?? "").trim();
};

const miniPinIcon = L.divIcon(PINCODE_SEARCH_MAP.MINI_PIN_ICON);
const normalizeServiceType = (value) => String(value ?? "").trim().toLowerCase();
const getServiceCoverage = (rows) => {
  const coverage = PINCODE_SEARCH_COVERAGE_CHIPS.reduce(
    (acc, item) => ({ ...acc, [item.key]: false }),
    {}
  );

  rows.forEach((row) => {
    if (!row?.isServiceable) return;

    const serviceType = normalizeServiceType(row.serviceType);
    PINCODE_SEARCH_COVERAGE_CHIPS.forEach((chip) => {
      if (chip.keywords.some((keyword) => serviceType.includes(keyword))) {
        coverage[chip.key] = true;
      }
    });
  });

  return coverage;
};

const formatCoordinates = (coords) => {
  if (!coords || !Number.isFinite(coords.lat) || !Number.isFinite(coords.lng)) {
    return PINCODE_SEARCH_COPY.MINI_MAP_POPUP_COORDINATE_FALLBACK;
  }

  return `${coords.lat.toFixed(PINCODE_SEARCH_MAP.COORDINATE_PRECISION)}, ${coords.lng.toFixed(PINCODE_SEARCH_MAP.COORDINATE_PRECISION)}`;
};

const MiniMapPopupCard = ({ pincode, coordinates, serviceCoverage }) => (
  <div className="pincode-mini-popup-card">
    <div className="pincode-mini-popup-title">
      {pincode || PINCODE_SEARCH_COPY.PAGE_TITLE}
    </div>
    <div className="pincode-mini-popup-subtitle">
      {PINCODE_SEARCH_COPY.MINI_MAP_POPUP_SUBTITLE}
    </div>
    <div className="pincode-mini-popup-chip-row">
      {PINCODE_SEARCH_COVERAGE_CHIPS.map((chip) => (
        <span
          key={chip.key}
          className={`pincode-mini-popup-chip pincode-mini-popup-chip-${chip.key} ${
            serviceCoverage?.[chip.key]
              ? "pincode-mini-popup-chip--active"
              : "pincode-mini-popup-chip--inactive"
          }`}
        >
          {chip.label}
        </span>
      ))}
    </div>
    <div className="pincode-mini-popup-coordinates">
      {formatCoordinates(coordinates)}
    </div>
  </div>
);

const PincodeSearch = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [serviceRows, setServiceRows] = useState([]);
  const [cityText, setCityText] = useState("");
  const [stateText, setStateText] = useState("");
  const [previewCoords, setPreviewCoords] = useState(null);
  const previewMarkerRef = useRef(null);

  useEffect(() => {
    const { pincodeData, pincode } = location.state || {};

    setServiceRows([]);
    setCityText("");
    setStateText("");
    setPreviewCoords(null);

    if (!pincodeData) return;

    const actualData = pincodeData?.data || {};
    const serviceDetails = Array.isArray(actualData.serviceDetails)
      ? actualData.serviceDetails
      : [];

    setCityText(actualData.city?.toUpperCase() || "");
    setStateText(actualData.state?.toUpperCase() || "");
    const primaryCoords = extractCoordinates(actualData);
    const firstServiceCoords = serviceDetails
      .map((item) => extractCoordinates(item))
      .find(Boolean);
    setPreviewCoords(primaryCoords || firstServiceCoords || null);

    const mappedData = serviceDetails
      .map((item, index) => {
        const hubNameValue = item?.[PINCODE_SEARCH_FIELD_KEYS.HUB_NAME];
        const hubName =
          typeof hubNameValue === "string" ? hubNameValue.trim() : "";
        const serviceType =
          item?.[PINCODE_SEARCH_FIELD_KEYS.SERVICE_TYPE]?.toUpperCase() ||
          PINCODE_SEARCH_COPY.DEFAULT_SERVICE_TYPE;
        const isServiceable = Boolean(item?.[PINCODE_SEARCH_FIELD_KEYS.SERVICEABLE]);

        if (
          serviceType === PINCODE_SEARCH_COPY.FILTERED_NON_SERVICEABLE_SERVICE_TYPE &&
          !isServiceable
        ) {
          return null;
        }
        if (!hubName) return null;

        return {
          key: `${pincode}-${index}`,
          hubName,
          serviceType,
          isServiceable,
        };
      })
      .filter(Boolean);

    setServiceRows(mappedData);
  }, [location.state]);

  const pincode = location.state?.pincode || "";
  const locationLabel = [cityText, stateText]
    .filter(Boolean)
    .join(PINCODE_SEARCH_COPY.LOCATION_SEPARATOR);
  const mapUrl = buildPincodeMapUrl(pincode);

  const groupedHubCards = Object.values(
    serviceRows.reduce((acc, row) => {
      const hubKey = row.hubName || PINCODE_SEARCH_COPY.HUB_NAME_FALLBACK;
      if (!acc[hubKey]) {
        acc[hubKey] = {
          hubName: hubKey,
          services: [],
          serviceableCount: 0,
        };
      }

      acc[hubKey].services.push({
        key: row.key,
        serviceType: row.serviceType,
        isServiceable: row.isServiceable,
      });

      if (row.isServiceable) {
        acc[hubKey].serviceableCount += 1;
      }

      return acc;
    }, {})
  );
  const mapCenter = previewCoords
    ? [previewCoords.lat, previewCoords.lng]
    : PINCODE_SEARCH_MAP.INDIA_CENTER;
  const mapZoom = previewCoords
    ? PINCODE_SEARCH_MAP.MINI_MAP_FOCUS_ZOOM
    : PINCODE_SEARCH_MAP.MINI_MAP_DEFAULT_ZOOM;
  const serviceCoverage = useMemo(
    () => getServiceCoverage(serviceRows),
    [serviceRows]
  );

  useEffect(() => {
    let cancelled = false;

    const loadMapCoordinates = async () => {
      if (!pincode) return;

      try {
        const response = await getAllNonServiceablePincodes();
        const allPincodes = Array.isArray(response?.data) ? response.data : [];
        const targetPincode = String(pincode).trim();
        const matchedPincode = allPincodes.find(
          (item) => extractPincode(item) === targetPincode
        );
        const mapCoords = matchedPincode ? extractCoordinates(matchedPincode) : null;

        if (!cancelled && mapCoords) {
          setPreviewCoords(mapCoords);
        }
      } catch {
        // Keep existing preview coordinates/default view if map dataset lookup fails.
      }
    };

    loadMapCoordinates();

    return () => {
      cancelled = true;
    };
  }, [pincode]);

  useEffect(() => {
    if (!previewCoords) return undefined;

    const timerId = window.setTimeout(() => {
      previewMarkerRef.current?.openPopup();
    }, PINCODE_SEARCH_MAP.PIN_POPUP_OPEN_DELAY_MS);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [previewCoords]);

  const goBackToPrevScreen = () => {
    if (window.history.length <= PINCODE_SEARCH_NAVIGATION.BACK_HISTORY_LIMIT) {
      window.close();
    } else {
      navigate(PINCODE_SEARCH_NAVIGATION.BACK_STEP);
    }
  };

  const getHubStatus = (hub) => {
    const isAllServiceable =
      hub.services.length > 0 && hub.serviceableCount === hub.services.length;
    const isNoneServiceable = hub.serviceableCount === 0;

    if (isAllServiceable) return PINCODE_SEARCH_STATUS.SERVICEABLE;
    if (isNoneServiceable) return PINCODE_SEARCH_STATUS.NON_SERVICEABLE;
    return PINCODE_SEARCH_STATUS.MIXED;
  };

  return (
    <div className="global-search-page pincode-search-page">
      <div className="p-2-tab">
        <Heading
          title={PINCODE_SEARCH_COPY.PAGE_TITLE}
          showBack={true}
          goBack={goBackToPrevScreen}
        />
      </div>

      <div className="global-search-shell">
        <Card className="global-search-panel global-search-header-card" bordered={false}>
          <div className="global-search-title-row">
            <div className="global-search-title-block">
              <h1>{pincode || PINCODE_SEARCH_COPY.PAGE_TITLE}</h1>
              {locationLabel ? <p>{locationLabel}</p> : null}
            </div>
            {groupedHubCards.length ? (
              <span className="global-search-badge">
                {getHubCountLabel(groupedHubCards.length)}
              </span>
            ) : null}
          </div>
          <div className="global-search-actions">
            <Button type="primary" onClick={() => navigate(mapUrl)}>
              <EnvironmentOutlined /> {PINCODE_SEARCH_COPY.OPEN_MAP_BUTTON}
            </Button>
            <Button onClick={goBackToPrevScreen}>{PINCODE_SEARCH_COPY.BACK_BUTTON}</Button>
          </div>
          {groupedHubCards.length ? (
            <div className="pincode-hub-subcard-grid">
              {groupedHubCards.map((hub, index) => {
                const status = getHubStatus(hub);

                return (
                  <div key={`${hub.hubName}-${index}`} className="pincode-hub-subcard">
                    <div className="global-search-card-head">
                      <div>
                        <h2 className="global-search-card-title">
                          {hub.hubName || getHubFallbackName(index)}
                        </h2>
                        <p className="global-search-card-subtitle">
                          {getServiceCountLabel(hub.services.length)}
                        </p>
                      </div>
                      <Tag className={`pincode-result-status ${status.className}`}>
                        {status.label}
                      </Tag>
                    </div>

                    <div className="pincode-service-chip-row">
                      {hub.services.map((service, serviceIndex) => (
                        <Tag
                          key={`${service.key}-${serviceIndex}`}
                          className={`pincode-service-chip ${
                            service.isServiceable
                              ? PINCODE_SEARCH_STATUS.SERVICEABLE.className
                              : PINCODE_SEARCH_STATUS.NON_SERVICEABLE.className
                          }`}
                        >
                          {service.serviceType}
                        </Tag>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="pincode-header-empty">
              <Empty
                description={getNoResultsDescription(pincode)}
              />
            </div>
          )}
        </Card>

        {pincode ? (
          <Card className="global-search-panel pincode-mini-map-card" bordered={false}>
            <div className="pincode-mini-map-head">
              <div>
                <p>
                  {previewCoords
                    ? PINCODE_SEARCH_COPY.MINI_MAP_PREVIEW_COPY
                    : PINCODE_SEARCH_COPY.MINI_MAP_DEFAULT_COPY}
                </p>
              </div>
              <Button type="primary" onClick={() => navigate(mapUrl)}>
                <EnvironmentOutlined /> {PINCODE_SEARCH_COPY.NAVIGATE_TO_MAP_BUTTON}
              </Button>
            </div>
            <div className="pincode-mini-map-frame-wrap">
              <MapContainer
                key={
                  previewCoords
                    ? `${previewCoords.lat}-${previewCoords.lng}`
                    : PINCODE_SEARCH_MAP.MINI_MAP_DEFAULT_KEY
                }
                center={mapCenter}
                zoom={mapZoom}
                style={{
                  height: PINCODE_SEARCH_MAP.MINI_MAP_HEIGHT,
                  width: PINCODE_SEARCH_MAP.MINI_MAP_WIDTH,
                }}
                scrollWheelZoom={false}
                zoomControl={false}
                attributionControl={false}
              >
                <TileLayer
                  attribution={PINCODE_SEARCH_MAP.TILE_ATTRIBUTION}
                  url={PINCODE_SEARCH_MAP.TILE_URL}
                />
                {previewCoords ? (
                  <Marker
                    position={mapCenter}
                    icon={miniPinIcon}
                    ref={previewMarkerRef}
                  >
                    <Popup
                      className="pincode-mini-popup"
                      autoPan={false}
                      closeButton={false}
                      maxWidth={PINCODE_SEARCH_MAP.PIN_POPUP_MAX_WIDTH}
                    >
                      <MiniMapPopupCard
                        pincode={pincode}
                        coordinates={previewCoords}
                        serviceCoverage={serviceCoverage}
                      />
                    </Popup>
                  </Marker>
                ) : null}
              </MapContainer>
            </div>
          </Card>
        ) : null}
      </div>
    </div>
  );
};

export default PincodeSearch;
